import React, { useState, useEffect, useCallback} from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  FileText,
  Plus,
  Search,
  Eye,
  Edit,
  CheckCircle2,
  XCircle,
  Clock,
  Loader2
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import axios from 'axios';
import DataTable from '../components/common/DataTable';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const getStatusColor = (status) => {
  const colors = {
    'OPEN': 'bg-orange-100 text-orange-800',
    'APPROVED': 'bg-green-100 text-green-800',
    'PARTIALLY_ORDERED': 'bg-yellow-100 text-yellow-800',
    'FULLY_ORDERED': 'bg-green-100 text-green-800',
    'REJECTED': 'bg-red-100 text-red-800',
    'CLOSED': 'bg-gray-100 text-gray-800'
  };
  return colors[status] || 'bg-gray-100 text-gray-800';
};

const getStatusIcon = (status) => {
  switch (status) {
    case 'OPEN': return <Clock className="w-4 h-4" />;
    case 'APPROVED':
    case 'FULLY_ORDERED': return <CheckCircle2 className="w-4 h-4" />;
    case 'REJECTED': return <XCircle className="w-4 h-4" />;
    default: return <FileText className="w-4 h-4" />;
  }
};

const formatDate = (dateString) => {
  if (!dateString) return '-';
  try {
    return new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return dateString;
  }
};

const PRList = () => {
  const navigate = useNavigate();
  const { canWrite } = useAuth();
  const [loading, setLoading] = useState(true);
  const [prs, setPrs] = useState([]);
  const [filteredPrs, setFilteredPrs] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');

  useEffect(() => {
    loadPRs();
  }, []);


  const loadPRs = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${BACKEND_URL}/api/purchase-requisitions`);
      setPrs(response.data);
    } catch (error) {
      console.error('Error loading PRs:', error);
      toast.error('Failed to load purchase requisitions');
    } finally {
      setLoading(false);
    }
  };

  const filterPRs = useCallback(() => {
    let filtered = [...prs];
    if (statusFilter !== 'ALL') {
      filtered = filtered.filter(pr => pr.status === statusFilter);
    }
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(pr =>
        pr.pr_number.toLowerCase().includes(term) ||
        (pr.created_by && pr.created_by.toLowerCase().includes(term))
      );
    }
    setFilteredPrs(filtered);
  }, [prs, searchTerm, statusFilter]);

  useEffect(() => {
    filterPRs();
  }, [filterPRs]);

  const columns = [
    { key: 'pr_number', header: 'PR Number', filterable: true, accessor: (pr) => pr.pr_number,
      render: (pr) => <span className="font-medium text-orange-600">{pr.pr_number}</span> },
    { key: 'description', header: 'Description',
      render: (pr) => (
        <div className="max-w-xs">
          <p className="text-sm text-gray-900 truncate">{pr.document_type || 'Standard PR'}</p>
          <p className="text-xs text-gray-500">{pr.total_items || pr.items?.length || 0} items • {pr.status || 'OPEN'}</p>
        </div>
      ) },
    { key: 'status', header: 'Status',
      render: (pr) => (
        <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(pr.status || 'OPEN')}`}>
          {getStatusIcon(pr.status || 'OPEN')}
          {(pr.status || 'OPEN').replace(/_/g, ' ')}
        </span>
      ) },
    { key: 'items', header: 'Items', accessor: (pr) => `${pr.items?.length || 0} ${(pr.items?.length || 0) === 1 ? 'item' : 'items'}` },
    { key: 'created_by', header: 'Created By', accessor: (pr) => pr.created_by },
    { key: 'created_at', header: 'Created At', accessor: (pr) => formatDate(pr.created_at) },
    { key: 'priority', header: 'Priority',
      render: (pr) => <span className="text-xs px-2 py-1 rounded bg-gray-100 text-gray-800">{pr.processing_status || 'ACTIVE'}</span> },
    { key: 'actions', header: 'Actions', align: 'right',
      render: (pr) => (
        <div className="flex items-center justify-end gap-2">
          <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); navigate(`/p2p/pr-view/${pr.pr_number}`); }} className="text-orange-600 hover:text-orange-800">
            <Eye className="w-4 h-4 mr-1" />View
          </Button>
          <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); navigate(`/p2p/pr-edit/${pr.pr_number}`); }} className="text-green-600 hover:text-green-800">
            <Edit className="w-4 h-4 mr-1" />Edit
          </Button>
        </div>
      ) },
  ];

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-screen">
          <div className="text-center">
            <Loader2 className="w-12 h-12 animate-spin text-orange-600 mx-auto mb-4" />
            <p className="text-gray-600">Loading purchase requisitions...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <FileText className="w-7 h-7 text-orange-600" />
                Purchase Requisitions
              </h1>
              <p className="text-sm text-gray-600 mt-1">{filteredPrs.length} of {prs.length} requisitions</p>
            </div>
            {canWrite('purchase_requisition') && (
            <Button onClick={() => navigate('/p2p/pr-create')} className="bg-orange-600 hover:bg-orange-700">
              <Plus className="w-4 h-4 mr-2" />
              Create PR
            </Button>
            )}
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm border p-4 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search by PR number, description, or creator..."
                  className="pl-10"
                />
              </div>
            </div>
            <div className="w-full md:w-48">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              >
                <option value="ALL">All Status</option>
                <option value="OPEN">Open</option>
                <option value="APPROVED">Approved</option>
                <option value="PARTIALLY_ORDERED">Partially Ordered</option>
                <option value="FULLY_ORDERED">Fully Ordered</option>
                <option value="REJECTED">Rejected</option>
              </select>
            </div>
          </div>
        </div>

        {/* PR Table */}
        <DataTable
          testid="pr-table"
          columns={columns}
          data={filteredPrs}
          rowKey={(pr) => pr.pr_number}
          emptyText={searchTerm || statusFilter !== 'ALL' ? 'No PRs found' : 'No purchase requisitions yet'}
          emptyHint={searchTerm || statusFilter !== 'ALL' ? 'Try adjusting your filters' : 'Create your first purchase requisition to get started'}
          rowProps={(pr) => ({ 'data-testid': `pr-${pr.pr_number}`, onClick: () => navigate(`/p2p/pr-view/${pr.pr_number}`), style: { cursor: 'pointer' } })}
        />

        {/* Summary Stats */}
        {prs.length > 0 && (
          <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-lg border p-4">
              <p className="text-sm text-gray-600">Total PRs</p>
              <p className="text-2xl font-bold text-gray-900">{prs.length}</p>
            </div>
            <div className="bg-white rounded-lg border p-4">
              <p className="text-sm text-gray-600">Open</p>
              <p className="text-2xl font-bold text-orange-600">{prs.filter(p => p.status === 'OPEN').length}</p>
            </div>
            <div className="bg-white rounded-lg border p-4">
              <p className="text-sm text-gray-600">Approved</p>
              <p className="text-2xl font-bold text-green-600">{prs.filter(p => p.status === 'APPROVED').length}</p>
            </div>
            <div className="bg-white rounded-lg border p-4">
              <p className="text-sm text-gray-600">Ordered</p>
              <p className="text-2xl font-bold text-orange-600">{prs.filter(p => ['PARTIALLY_ORDERED', 'FULLY_ORDERED'].includes(p.status)).length}</p>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default PRList;
